import React from 'react';

import { getAIProviderIcon } from '../lib/aiProviderIcons';

interface IconProps {
  size?: number;
  className?: string;
  /** Accepted for backwards compatibility with the prior SVG icons; ignored for PNG. */
  strokeWidth?: number;
}

/**
 * AI Council member icons.
 *
 * As of 2026-05-23 these render the branded PNG assets at
 * public/ai-icons/*.png instead of generic line-art SVGs. The component API
 * (size / className / strokeWidth) is preserved so existing call sites work
 * unchanged.
 *
 * Provider -> icon mapping:
 *   BrainIcon    -> ChatGPT  (/ai-icons/chatgpt-brain.png)
 *   OwlIcon      -> Claude   (/ai-icons/claude-owl.png)
 *   ElephantIcon -> Gemini   (/ai-icons/gemini-elephant.png)
 *   CompassIcon  -> Perplexity (/ai-icons/perplexity-compass.png)
 */

interface PngIconProps extends IconProps {
  src: string;
  alt: string;
}

// Light mode: white icons need a dark backdrop or they vanish on white cards.
// Dark mode: backdrop is transparent so the icon sits naturally on whatever
// colored pill the caller already provides.
const PngIcon: React.FC<PngIconProps> = ({ size = 28, className = '', src, alt }) => (
  <span
    role="img"
    aria-label={alt}
    title={alt}
    className={`ai-council-icon inline-flex items-center justify-center ${className}`}
    style={{ width: size, height: size, flexShrink: 0 }}
    data-ai-icon={alt}
  >
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
      loading="lazy"
      decoding="async"
      draggable={false}
    />
  </span>
);

export const OwlIcon: React.FC<IconProps> = (props) => {
  const i = getAIProviderIcon('claude');
  return <PngIcon {...props} src={i.src!} alt={i.alt} />;
};

export const ElephantIcon: React.FC<IconProps> = (props) => {
  const i = getAIProviderIcon('gemini');
  return <PngIcon {...props} src={i.src!} alt={i.alt} />;
};

export const BrainIcon: React.FC<IconProps> = (props) => {
  const i = getAIProviderIcon('chatgpt');
  return <PngIcon {...props} src={i.src!} alt={i.alt} />;
};

export const CompassIcon: React.FC<IconProps> = (props) => {
  const i = getAIProviderIcon('perplexity');
  return <PngIcon {...props} src={i.src!} alt={i.alt} />;
};

/**
 * Generic provider-by-name icon. Accepts any provider/model/role string and
 * resolves it through `getAIProviderIcon`. Returns initials in a small badge
 * if no known provider matches, so the UI never renders a broken image.
 */
export const AIProviderIconView: React.FC<IconProps & { provider: string | null | undefined }> = ({
  provider,
  size = 24,
  className = '',
}) => {
  const i = getAIProviderIcon(provider);
  if (i.src) {
    return <PngIcon size={size} className={className} src={i.src} alt={i.alt} />;
  }
  return (
    <span
      role="img"
      aria-label={i.alt}
      title={i.label}
      className={`ai-council-icon inline-flex items-center justify-center rounded-full bg-slate-700 text-slate-100 text-[10px] font-bold uppercase ${className}`}
      style={{ width: size, height: size, flexShrink: 0 }}
    >
      {i.alt}
    </span>
  );
};
